/**
 * Sélecteur de période.
 *
 * Une seule rangée, au-dessus de tout le contenu qu'elle cadre — jamais dans
 * une carte, jamais par graphique. Les préréglages d'abord, la saisie manuelle
 * ensuite : c'est le contrôle que tout le monde attrape en premier.
 *
 * Les périodes s'arrêtent au dernier jour de données de la page : hier pour
 * l'usage de l'app (sync nocturne), aujourd'hui pour le reste (cf. `DataScope`
 * dans `lib/dates.ts`).
 */

import { latestDataDay, maxSelectableDay, presetsForScope, type DataScope } from '../lib/dates';
import type { SelectedPeriod } from '../types';

interface PeriodPickerProps {
  value: SelectedPeriod;
  onChange: (period: SelectedPeriod) => void;
  /** Jusqu'où les données de la page existent : borne des préréglages et du début. */
  scope: DataScope;
}

export function PeriodPicker({ value, onChange, scope }: PeriodPickerProps) {
  // Début : au plus le dernier jour de données (il faut au moins un jour).
  // Fin : jusqu'au dernier jour du mois en cours — cf. `maxSelectableDay`.
  const maxDate = latestDataDay(scope);
  const maxEnd = maxSelectableDay();
  const presets = presetsForScope(scope);

  const selectedPreset = presets.find((preset) => preset.id === value.presetId);

  return (
    <div className="filters">
      {/* Sur téléphone, une liste déroulante remplace la rangée de boutons
          (CSS : .preset-group--period / .period-select). Une période saisie à
          la main n'a pas de préréglage : l'option « Personnalisée » le dit. */}
      <div className="field period-select">
        <label className="field__label" htmlFor="period-preset">
          Période
        </label>
        <select
          id="period-preset"
          value={selectedPreset?.id ?? 'custom'}
          onChange={(event) => {
            const preset = presets.find((item) => item.id === event.target.value);
            if (preset) onChange({ ...preset.range(scope), presetId: preset.id });
          }}
        >
          {!selectedPreset && <option value="custom">Personnalisée</option>}
          {presets.map((preset) => (
            <option key={preset.id} value={preset.id}>
              {preset.label}
            </option>
          ))}
        </select>
      </div>

      <div className="preset-group preset-group--period" role="group" aria-label="Période prédéfinie">
        {presets.map((preset) => (
          <button
            key={preset.id}
            type="button"
            className="preset"
            aria-pressed={value.presetId === preset.id}
            onClick={() => onChange({ ...preset.range(scope), presetId: preset.id })}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="field">
        <label className="field__label" htmlFor="period-from">
          Du
        </label>
        <input
          id="period-from"
          type="date"
          value={value.from}
          max={value.to < maxDate ? value.to : maxDate}
          onChange={(event) =>
            onChange({ from: event.target.value, to: value.to, presetId: null })
          }
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="period-to">
          Au
        </label>
        <input
          id="period-to"
          type="date"
          value={value.to}
          min={value.from}
          max={maxEnd}
          onChange={(event) =>
            onChange({ from: value.from, to: event.target.value, presetId: null })
          }
        />
      </div>
    </div>
  );
}
